// Store/Slices/documentSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import BaseUrl from '../../../BaseUrl';

const API_BASE_URL = BaseUrl.templatesurl;

// Async thunks for categories
export const fetchCategories = createAsyncThunk(
  'documents/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}DocumentCategories/GetAllCategories`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addCategory = createAsyncThunk(
  'documents/addCategory',
  async (categoryName, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}DocumentCategories/PostCategory`,
        { Name: categoryName }
      );
      
      // After successfully adding the category, fetch the updated list
      await dispatch(fetchCategories());
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateCategory = createAsyncThunk(
  'documents/updateCategory',
  async ({ id, name }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}DocumentCategories/UpdateCategory`,
        { Id: id, Name: name }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunks for documents
export const uploadDocument = createAsyncThunk(
  'documents/uploadDocument',
  async ({ categoryId, name, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('Name', name);
      formData.append('CategoryId', categoryId);
      formData.append('File', file);
      
      const response = await axios.post(
        `${API_BASE_URL}Documents`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchDocuments = createAsyncThunk(
  'documents/fetchDocuments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}Documents/GetAllDocuments`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// NEW: Fetch document by name
export const fetchDocumentByName = createAsyncThunk(
  'documents/fetchDocumentByName',
  async (documentName, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}Documents/${documentName}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Initial state
const initialState = {
  categories: [],
  documents: [],
  selectedCategoryId: null,
  isLoading: false,
  isUploading: false,
  isFetchingDocuments: false,
  isSearching: false,
  searchResults: null,
  error: null,
  successMessage: null,
};

// Slice
const documentSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
    setSelectedCategory: (state, action) => {
      state.selectedCategoryId = action.payload;
    },
    resetState: (state) => {
      state.isLoading = false;
      state.isUploading = false;
      state.isFetchingDocuments = false;
      state.isSearching = false;
      state.error = null;
      state.successMessage = null;
    },
    clearSearchResults: (state) => {
      state.searchResults = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch categories
      .addCase(fetchCategories.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.isLoading = false;
        state.categories = action.payload || [];
        if (action.payload?.length > 0 && !state.selectedCategoryId) {
          state.selectedCategoryId = action.payload[0].Id;
        }
        if (state.selectedCategoryId && !state.categories.some(cat => cat.Id === state.selectedCategoryId)) {
          state.selectedCategoryId = state.categories[0]?.Id || null;
        }
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch categories';
        state.categories = [];
      })
      
      // Add category
      .addCase(addCategory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(addCategory.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.successMessage = `Category "${action.payload.Name || 'New Category'}" added successfully`;
        }
      })
      .addCase(addCategory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to add category';
      })
      
      // Upload document
      .addCase(uploadDocument.pending, (state) => {
        state.isUploading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        state.isUploading = false;
        if (action.payload) {
          if (action.payload.Id || action.payload.id) {
            state.documents.push(action.payload);
          }
        }
        state.successMessage = 'Document uploaded successfully';
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        state.isUploading = false;
        state.error = action.payload || 'Failed to upload document';
      })
      
      // Fetch documents
      .addCase(fetchDocuments.pending, (state) => {
        state.isFetchingDocuments = true;
        state.error = null;
        state.searchResults = null; // Clear search results when fetching all
      })
      .addCase(fetchDocuments.fulfilled, (state, action) => {
        state.isFetchingDocuments = false;
        state.documents = action.payload || [];
      })
      .addCase(fetchDocuments.rejected, (state, action) => {
        state.isFetchingDocuments = false;
        state.error = action.payload || 'Failed to fetch documents';
        state.documents = [];
      })
      
      // Fetch document by name
      .addCase(fetchDocumentByName.pending, (state) => {
        state.isSearching = true;
        state.error = null;
        state.searchResults = null;
      })
      .addCase(fetchDocumentByName.fulfilled, (state, action) => {
        state.isSearching = false;
        state.searchResults = action.payload;
        if (action.payload) {
          state.successMessage = `Document "${action.payload.Name}" found`;
        }
      })
      .addCase(fetchDocumentByName.rejected, (state, action) => {
        state.isSearching = false;
        state.error = action.payload || 'Document not found';
        state.searchResults = null;
      });
  },
});

export const { clearMessages, setSelectedCategory, resetState, clearSearchResults } = documentSlice.actions;
export default documentSlice.reducer;